import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

/**
 * Returns a stable callback that invalidates all stale contract-read caches
 * after a stake write operation (stake, claim reward, withdraw stake).
 *
 * Three keys are invalidated:
 * 1. `['readContract']` — wagmi-internal prefix: forces getUserStake,
 *    getUserStakeIds, getUserTokens, and balanceOf reads to re-fetch from
 *    chain. Without this, the queryFn closure in useFetchStakeContractData
 *    still reads stale wagmi data even after TQ cache is cleared.
 * 2. `['stake_contract_data']` — TanStack Query key for
 *    useFetchStakeContractData: forces the aggregate queryFn to re-run with
 *    the freshly loaded wagmi data above.
 * 3. `['stake', 'user-wrappers']` — TanStack Query key for useUserWrappers:
 *    an NFT moves from "owned wrappers" to "staked" on stake success, so the
 *    wrapper list must refresh.
 *
 * IMPORTANT: Do NOT use the bare `['stake']` key — it prefix-matches
 * `['stake', 'user-wrappers']` but misses `['stake_contract_data']`.
 */
export function useInvalidateStakeReads(): () => void {
  const queryClient = useQueryClient();

  return useCallback(() => {
    // Force wagmi reads to re-fetch from chain (resolves closure-stale problem)
    void queryClient.invalidateQueries({ queryKey: ["readContract"] });
    // Force aggregate stake data re-computation with fresh wagmi data
    void queryClient.invalidateQueries({ queryKey: ["stake_contract_data"] });
    // Force wrapper list refresh (NFT ownership changes after stake)
    void queryClient.invalidateQueries({
      queryKey: ["stake", "user-wrappers"],
    });
  }, [queryClient]);
}
